import { useEffect, useRef } from "react";
import { hubConfig } from "./routes";

function goToPath(path) {
  if (!path) return;

  if (/^https?:\/\//i.test(path)) {
    let normalizedExternalPath = path;
    try {
      const url = new URL(path);
      const normalizedHostname = url.hostname.replace(/_/g, "");
      if (normalizedHostname !== url.hostname) {
        url.hostname = normalizedHostname;
        normalizedExternalPath = url.toString();
      }
    } catch {
      normalizedExternalPath = path;
    }
    window.location.assign(normalizedExternalPath);
    return;
  }

  const resolvedPath = path.startsWith("/") ? path : `/${path}`;
  window.location.assign(resolvedPath);
}

function isExternalTarget(target) {
  return /^https?:\/\//i.test(target || "");
}

function getCardTarget(perms, item) {
  return perms[item.permKey]?.redir || item.redir || item.path;
}

function getSubMenuTarget(perms, cardPermKey, subItem) {
  const permsSubMenu = perms[cardPermKey]?.sub_menus?.[subItem.permKey];
  if (permsSubMenu && typeof permsSubMenu === "object") {
    return permsSubMenu.redir || subItem.redir || subItem.path;
  }
  return subItem.redir || subItem.path;
}

function canViewCard(perms, permKey) {
  // If perms haven't loaded yet, show everything
  if (!permKey || Object.keys(perms).length === 0) return true;
  return perms[permKey]?.can_view !== false;
}

function canViewSubMenu(perms, cardPermKey, subPermKey) {
  if (!subPermKey || Object.keys(perms).length === 0) return true;
  const subMenus = perms[cardPermKey]?.sub_menus;
  if (!subMenus) return true;
  return subMenus[subPermKey] !== false;
}

export default function Home({
  user,
  perms = {},
  isAuthenticated,
  onLogin,
  onLogout,
}) {
  const hasAutoRedirected = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || hasAutoRedirected.current) return;
    if (Object.keys(perms).length === 0) return;

    const autoRedirectItem = hubConfig.navButtons.find((item) => {
      if (!canViewCard(perms, item.permKey)) return false;
      const itemPerms = perms[item.permKey] || {};
      return (
        itemPerms.do_auto_redirect === true ||
        itemPerms["do_auto-redirect"] === true
      );
    });

    if (!autoRedirectItem) return;

    const target = getCardTarget(perms, autoRedirectItem);
    if (!target) return;

    hasAutoRedirected.current = true;
    goToPath(target);
  }, [isAuthenticated, perms]);

  return (
    <div className="hub-layout">
      <aside className="hub-sidebar">
        <div className="hub-brand">
          <img
            src={hubConfig.brandIcon}
            alt="Company icon"
            className="hub-brand-logo"
          />
          <span className="hub-brand-text">{hubConfig.appName}</span>
        </div>
        <div className="hub-subtitle">{hubConfig.subtitle}</div>

        <div className="hub-user-card">
          <div className="hub-user-name">{user?.username || "User"}</div>
          {user?.email ? (
            <div className="hub-user-email">{user.email}</div>
          ) : null}
          <button
            type="button"
            className="hub-login-button"
            onClick={isAuthenticated ? onLogout : onLogin}
          >
            {isAuthenticated ? "Logout" : "Login"}
          </button>
        </div>
      </aside>

      <main className="hub-main">
        <h1 className="hub-title">Available Apps</h1>
        <div className="hub-grid">
          {hubConfig.navButtons
            .filter((item) => canViewCard(perms, item.permKey))
            .map((item) => {
              const cardTarget = getCardTarget(perms, item);
              const isExternalCard = isExternalTarget(cardTarget);
              const visibleSubs = (item.subMenus || []).filter((sub) =>
                canViewSubMenu(perms, item.permKey, sub.permKey),
              );
              return (
                <div key={item.id} className="hub-card">
                  <button
                    type="button"
                    className={`hub-card-main${isExternalCard ? " hub-card-main-external" : ""}`}
                    onClick={() => goToPath(cardTarget)}
                  >
                    <span className="hub-card-title">{item.label}</span>
                    <span className="hub-card-text">
                      {item.description}
                      {isExternalCard ? " • Opens a new login page" : ""}
                    </span>
                  </button>

                  {visibleSubs.length ? (
                    <div className="hub-submenu-grid">
                      {visibleSubs.map((subItem) => {
                        const subTarget = getSubMenuTarget(
                          perms,
                          item.permKey,
                          subItem,
                        );
                        const isExternalSub = isExternalTarget(subTarget);
                        return (
                          <button
                            key={subItem.id}
                            type="button"
                            className={`hub-submenu-box${isExternalSub ? " hub-submenu-box-external" : ""}`}
                            onClick={() => goToPath(subTarget)}
                          >
                            {subItem.label}
                            {isExternalSub ? " ↗" : ""}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      </main>
    </div>
  );
}
